import { getRealmLayerInfo, type RealmId } from "@xiuxian/game-core";
import type { TreasureKind } from "@xiuxian/game-protocol";
import { type Locale, type TranslationKey, translate } from "./index.js";

export type PeriodLeaderboardKind = "day" | "week" | "month";

const REALM_NAME_KEYS: Record<RealmId, TranslationKey> = {
  qi: "realm.qi",
  foundation: "realm.foundation",
  core: "realm.core",
  nascent: "realm.nascent",
  spirit: "realm.spirit",
};

const REALM_SHORT_NAME_KEYS: Record<RealmId, TranslationKey> = {
  qi: "realm.qiShort",
  foundation: "realm.foundationShort",
  core: "realm.coreShort",
  nascent: "realm.nascentShort",
  spirit: "realm.spiritShort",
};

const TREASURE_TEXT_KEYS: Record<
  TreasureKind,
  { name: TranslationKey; shortEffect: TranslationKey }
> = {
  qiankun_ruler: {
    name: "treasure.qiankun_ruler.name",
    shortEffect: "treasure.qiankun_ruler.shortEffect",
  },
  hunyuan_fan: {
    name: "treasure.hunyuan_fan.name",
    shortEffect: "treasure.hunyuan_fan.shortEffect",
  },
  spirit_gourd: {
    name: "treasure.spirit_gourd.name",
    shortEffect: "treasure.spirit_gourd.shortEffect",
  },
  thunder_sword_box: {
    name: "treasure.thunder_sword_box.name",
    shortEffect: "treasure.thunder_sword_box.shortEffect",
  },
  myriad_sword_token: {
    name: "treasure.myriad_sword_token.name",
    shortEffect: "treasure.myriad_sword_token.shortEffect",
  },
  wind_thunder_boots: {
    name: "treasure.wind_thunder_boots.name",
    shortEffect: "treasure.wind_thunder_boots.shortEffect",
  },
  black_tortoise_armor: {
    name: "treasure.black_tortoise_armor.name",
    shortEffect: "treasure.black_tortoise_armor.shortEffect",
  },
  ascension_pearl: {
    name: "treasure.ascension_pearl.name",
    shortEffect: "treasure.ascension_pearl.shortEffect",
  },
};

const PERIOD_LABEL_KEYS: Record<PeriodLeaderboardKind, TranslationKey> = {
  day: "leaderboard.periodLabel.day",
  week: "leaderboard.periodLabel.week",
  month: "leaderboard.periodLabel.month",
};

const PERIOD_TITLE_KEYS: Record<PeriodLeaderboardKind, TranslationKey> = {
  day: "leaderboard.periodTitle.day",
  week: "leaderboard.periodTitle.week",
  month: "leaderboard.periodTitle.month",
};

export function formatRealmLayer(locale: Locale, level: number): string {
  const info = getRealmLayerInfo(level);
  return translate(locale, "realm.layer", {
    realm: translate(locale, REALM_NAME_KEYS[info.realm.id]),
    layer: info.layer,
  });
}

/**
 * Compact realm + layer label for narrow contexts such as the leaderboard.
 * English uses single-word realm names ("Spirit 10") so the cell does not
 * overflow; Chinese stays identical to {@link formatRealmLayer}.
 */
export function formatRealmLayerCompact(locale: Locale, level: number): string {
  const info = getRealmLayerInfo(level);
  return translate(locale, "realm.layerCompact", {
    realm: translate(locale, REALM_SHORT_NAME_KEYS[info.realm.id]),
    layer: info.layer,
  });
}

export function formatAscensionTarget(locale: Locale): string {
  return translate(locale, "realm.ascension");
}

export function formatTreasureName(locale: Locale, kind: TreasureKind): string {
  return translate(locale, TREASURE_TEXT_KEYS[kind].name);
}

export function formatTreasureShortEffect(
  locale: Locale,
  kind: TreasureKind,
): string {
  return translate(locale, TREASURE_TEXT_KEYS[kind].shortEffect);
}

export function formatPeriodLabel(
  locale: Locale,
  period: PeriodLeaderboardKind,
): string {
  return translate(locale, PERIOD_LABEL_KEYS[period]);
}

export function formatPeriodTitle(
  locale: Locale,
  period: PeriodLeaderboardKind,
): string {
  return translate(locale, PERIOD_TITLE_KEYS[period]);
}

export function formatDefaultPlayerName(locale: Locale): string {
  return translate(locale, "leaderboard.defaultPlayerName");
}

export function formatScoreValue(locale: Locale, value: number): string {
  const score = Math.max(0, Math.floor(value));
  if (locale === "en") {
    return formatEnglishScore(score);
  }
  return formatChineseScore(score);
}

function formatChineseScore(score: number): string {
  if (score >= 100_000_000) {
    return `${(score / 100_000_000).toFixed(1)}亿`;
  }
  if (score >= 10_000) {
    return `${(score / 10_000).toFixed(score >= 100_000 ? 0 : 1)}万`;
  }
  return score.toString();
}

function formatEnglishScore(score: number): string {
  if (score >= 1_000_000_000) {
    return `${formatCompactNumber(score / 1_000_000_000, score >= 10_000_000_000)}B`;
  }
  if (score >= 1_000_000) {
    return `${formatCompactNumber(score / 1_000_000, score >= 100_000_000)}M`;
  }
  if (score >= 1_000) {
    return `${formatCompactNumber(score / 1_000, score >= 100_000)}K`;
  }
  return score.toString();
}

function formatCompactNumber(value: number, wholeNumber: boolean): string {
  return value.toFixed(wholeNumber ? 0 : 1).replace(/\.0$/, "");
}
